import { useEffect, useState, useRef } from "react";
import http from "../Api/http";
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { Navigate } from "react-router-dom";
import { FaBusAlt, FaTruck } from 'react-icons/fa';
import { motion } from "framer-motion";

export default function TrafficJams() {
    const { t, i18n } = useTranslation();
    const { user, loadingUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [selectedStep, setSelectedStep] = useState(null);
    const dialogRef = useRef();

    useEffect(() => {
        if (!user) return;
        async function fetchData() {
            setLoading(true);
            const lang = i18n.language || 'ar';
            const res = await http.getJsonAxios(http.actions.Traffic.GetStepsDetails, { lang }, t);
            if (res.status === 200) setData(res.data || []);
            setLoading(false);
        }
        fetchData();
        const interval = setInterval(fetchData, window.API_URL.interval_time || 30000);
        return () => clearInterval(interval);
    }, [i18n.language, user]);

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (selectedStep && dialogRef.current && !dialogRef.current.contains(e.target)) {
                setSelectedStep(null);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [selectedStep]);

    if (loadingUser) return null;
    if (!user || (user.Role !== 1 && user.Role !== 2)) return <Navigate to="/" />;

    const busSteps = extractFilteredSteps(data, id => [2, 3, 4].includes(id));
    const truckSteps = extractFilteredSteps(data, id => id === 1);

    return (
        <div className="min-h-screen pt-8 pb-20 bg-background" dir={t('dir')}>
            {/* {loading && <ProgressSpinner loading={loading} />} */}
            {/* عنوان About */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-16"
            >
                <h1 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900 dark:text-white">
                    {t('trafficJams')}
                </h1>
                <div className="h-1.5 w-24 bg-accent mx-auto rounded-full" />
            </motion.div>

            {/* Buses & Trucks Section */}
            <div className="grid md:grid-cols-2 gap-12 items-start">
                <StepSection
                    icon={<FaBusAlt className="text-blue-500 h-8 w-8" />}
                    title={t("buses")}
                    steps={busSteps}
                    onStepClick={setSelectedStep}
                />
                <StepSection
                    icon={<FaTruck className="text-green-600 h-8 w-8" />}
                    title={t("trucks")}
                    steps={truckSteps}
                    onStepClick={setSelectedStep}
                />
            </div>

            {/* Modal */}
            {selectedStep && (
                <StepDetailsModal step={selectedStep} onClose={() => setSelectedStep(null)} dialogRef={dialogRef} t={t} />
            )}
        </div>
    );
}

// Filter steps helper
function extractFilteredSteps(data, typeFilterFn) {
    return data.map(step => {
        const filteredTypes = step.BusTypes.filter(bt => typeFilterFn(bt.BusTypeId));
        return filteredTypes.length > 0 ? { ...step, BusTypes: filteredTypes } : null;
    }).filter(Boolean);
}

// Step Section
function StepSection({ icon, title, steps, onStepClick }) {
    const totalCount = steps.reduce((acc, step) => acc + step.BusTypes.reduce((a, bt) => a + (bt.BusesCount || 0), 0), 0);

    return (
        <div className="flex-1 flex flex-col bg-white border rounded-lg shadow-md">
            <div className="p-4 border-b flex flex-col items-center justify-center gap-2">
                {icon}
                <h2 className="text-lg text-center">{title} ({totalCount})</h2>
            </div>

            <div className="flex-1 p-4 overflow-y-auto">
                <div className="flex flex-wrap justify-center gap-3">
                    {steps.map(step => (
                        <div
                            key={step.StepID}
                            onClick={() => {
                                const allBusDetails = step.BusTypes.flatMap(bt => bt.BusDetails || []);
                                onStepClick({ StepName: step.StepName, BusDetails: allBusDetails });
                            }}
                            className={`flex flex-col items-center justify-center cursor-pointer font-bold text-white text-center transition-transform hover:scale-105 rounded-full ${getCircleColor(step.StepID, step.BusTypes[0])} ${getCircleSize(step.BusTypes[0].BusTypeId, steps.length)}`}
                        >
                            <span>{step.StepName}</span>
                            <span>({step.BusTypes.reduce((sum, bt) => sum + (bt.BusesCount || 0), 0)})</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// Modal
function StepDetailsModal({ step, onClose, dialogRef, t }) {
    return (
        <div dir={t('dir')} className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
            <div ref={dialogRef} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg max-w-3xl w-full overflow-auto relative p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-center w-full">📍 {step.StepName}</h2>
                    <button className="absolute top-2 right-4 text-red-600 text-xl" onClick={onClose}><strong>✖</strong></button>
                </div>

                <div className="overflow-x-auto max-h-[500px]">
                    <table className="min-w-full border border-gray-300 text-sm text-center">
                        <thead className="bg-gray-200">
                            <tr>
                                <th className="px-2 py-1 border">{t("orderNo")}</th>
                                <th className="px-2 py-1 border">{t("busNo")}</th>
                                <th className="px-2 py-1 border">{t("busType")}</th>
                                <th className="px-2 py-1 border">{t("driverName")}</th>
                                <th className="px-2 py-1 border">{t("arrivalTime")}</th>
                                <th className="px-2 py-1 border">{t("duration")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {step.BusDetails
                                .sort((a, b) => new Date(a.ArrivalTime) - new Date(b.ArrivalTime))
                                .map((bus, i) => (
                                    <tr key={i} className="hover:bg-gray-50">
                                        <td className="px-2 py-1 border">{i + 1}</td>
                                        <td className="px-2 py-1 border">{bus.BusNo}</td>
                                        <td className="px-2 py-1 border">{bus.BusType}</td>
                                        <td className="px-2 py-1 border">{bus.DriverName}</td>
                                        <td className="px-2 py-1 border">{new Date(bus.ArrivalTime).toLocaleTimeString("en-EG", { hour: "2-digit", minute: "2-digit", hour12: false })}</td>
                                        <td className="px-2 py-1 border">{bus.Duration}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// Circle helpers
function getCircleColor(stepId, bt) {
    if (bt.BusesCount === 0) return "bg-red-600";
    if (bt.BusesCount > bt.MaxAllowed) return "bg-green-600";
    return "bg-orange-500";
}

function getCircleSize(busTypeId, count) {
    if (count <= 6) return "w-40 h-40 text-sm";
    if (count <= 10) return "w-40 h-40 text-base";
    return "w-40 h-40 text-lg";
}
